class LuxdeiMaterialTabs extends HTMLElement {
  #abortController = null;
  #activeIndex = 0;
  #touchStart = null;

  connectedCallback() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const tabs = this.tabs;
    if (!tabs.length) return;

    const selectedIndex = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    this.#activeIndex = selectedIndex >= 0 ? selectedIndex : 0;

    const options = { signal: this.#abortController.signal };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.select(index), options);
      tab.addEventListener('keydown', (event) => this.#handleKeydown(event, index), options);
    });

    this.addEventListener('pointerdown', this.#handlePointerDown, options);
    this.addEventListener('pointerup', this.#handlePointerUp, options);
    this.addEventListener('pointercancel', this.#clearPointer, options);
    document.addEventListener('shopify:block:select', this.#handleBlockSelect, options);

    this.#render({ instant: true });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
  }

  get tabs() {
    return Array.from(this.querySelectorAll('[role="tab"]'));
  }

  get panels() {
    return Array.from(this.querySelectorAll('[role="tabpanel"]'));
  }

  select(index, { focus = false, instant = false } = {}) {
    if (index < 0 || index >= this.tabs.length || index === this.#activeIndex) {
      if (focus) this.tabs[index]?.focus();
      return;
    }

    this.#activeIndex = index;
    this.#render({ instant });

    if (focus) this.tabs[index]?.focus();
  }

  #render({ instant = false } = {}) {
    if (instant) this.setAttribute('data-instant', '');

    this.tabs.forEach((tab, index) => {
      const isActive = index === this.#activeIndex;
      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    this.panels.forEach((panel, index) => {
      const isActive = index === this.#activeIndex;
      panel.setAttribute('aria-hidden', String(!isActive));
      panel.toggleAttribute('inert', !isActive);
    });

    if (instant) {
      requestAnimationFrame(() => requestAnimationFrame(() => this.removeAttribute('data-instant')));
    }
  }

  #handleKeydown(event, index) {
    const lastIndex = this.tabs.length - 1;
    let nextIndex;

    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = index === 0 ? lastIndex : index - 1;
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = index === lastIndex ? 0 : index + 1;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = lastIndex;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.select(nextIndex, { focus: true });
  }

  #handlePointerDown = (event) => {
    if (event.pointerType === 'mouse' || event.target.closest('[role="tab"]')) return;
    this.#touchStart = { x: event.clientX, y: event.clientY };
  };

  #handlePointerUp = (event) => {
    if (!this.#touchStart) return;

    const deltaX = event.clientX - this.#touchStart.x;
    const deltaY = event.clientY - this.#touchStart.y;
    this.#touchStart = null;

    if (Math.abs(deltaX) < 48 || Math.abs(deltaX) <= Math.abs(deltaY)) return;

    const direction = deltaX < 0 ? 1 : -1;
    const nextIndex = (this.#activeIndex + direction + this.tabs.length) % this.tabs.length;
    this.select(nextIndex);
  };

  #clearPointer = () => {
    this.#touchStart = null;
  };

  #handleBlockSelect = (event) => {
    const block = event.target.closest('[data-material-block-id]');
    if (!block || !this.contains(block)) return;

    const index = this.tabs.findIndex((tab) => tab.dataset.materialBlockId === block.dataset.materialBlockId);
    if (index >= 0) this.select(index, { instant: true });
  };
}

if (!customElements.get('luxdei-material-tabs')) {
  customElements.define('luxdei-material-tabs', LuxdeiMaterialTabs);
}
